import React, { useEffect, useState } from 'react';
import {
  Box, Stack, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  Divider, Alert, TextField, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Paper, ToggleButton, ToggleButtonGroup, MenuItem, Chip,
} from '@mui/material';
import {
  CheckCircleOutline, WarningAmberOutlined, BlockOutlined, FingerprintOutlined,
} from '@mui/icons-material';
import { StyledCellHeader } from '../../../components/StyledCellHeader';
import { resumenCampos, esSerializado, nombreBusqueda } from './utils';

const MODO = { OK: 'ok', DISCREPANCIA: 'discrepancia', RECHAZADO: 'rechazado' };

const authHeaders = () => ({
  'Content-Type': 'application/json',
  'X-Usuario-ID': localStorage.getItem('id'),
  'X-company-ID': localStorage.getItem('idcompany'),
});

/**
 * Dialog de recepción con 3 modos por item:
 *   - OK:           cantidad_recibida = enviada, sin motivo
 *   - DISCREPANCIA: cantidad_recibida < enviada, motivo obligatorio
 *   - RECHAZADO:    cantidad_recibida = 0, motivo obligatorio
 * Al confirmar, si TODOS están rechazados → estado `rechazada`, si no → `recibida`.
 */
export default function RecibirDialog({ open, onClose, transferencia, onDone }) {
  const [items, setItems] = useState([]);
  const [observaciones, setObservaciones] = useState('');
  const [motivos, setMotivos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    fetch(`${process.env.REACT_APP_SERVER_URL}/api/transferencias_mercancia/motivos_rechazo`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => setMotivos(Array.isArray(d) ? d : []))
      .catch(() => setMotivos([]));
  }, [open]);

  useEffect(() => {
    if (!open || !transferencia) return;
    setItems(
      (transferencia.items || []).map(it => ({
        id: it.id,
        producto_nombre: it.producto_nombre,
        cantidad_enviada: it.cantidad_enviada,
        cantidad_recibida: it.cantidad_enviada,
        modo: MODO.OK,
        motivo_rechazo_codigo: '',
        nota_item: '',
        // preservo metadata para mostrar campos personalizados
        categoria_tipo: it.categoria_tipo,
        categoria_fields: it.categoria_fields,
        producto_campos: it.producto_campos,
      })),
    );
    setObservaciones('');
    setError('');
  }, [open, transferencia]);

  const handleModo = (idx, modo) => {
    setItems(items.map((it, i) => {
      if (i !== idx) return it;
      if (modo === MODO.OK)           return { ...it, modo, cantidad_recibida: it.cantidad_enviada, motivo_rechazo_codigo: '' };
      if (modo === MODO.RECHAZADO)    return { ...it, modo, cantidad_recibida: 0 };
      return { ...it, modo }; // DISCREPANCIA: mantiene lo que el user escriba
    }));
  };

  const handleCantidad = (idx, value) => {
    const enviada = items[idx].cantidad_enviada;
    const n = Math.max(0, Math.min(parseInt(value, 10) || 0, enviada));
    setItems(items.map((it, i) => i === idx ? { ...it, cantidad_recibida: n, modo: n === 0 ? MODO.RECHAZADO : (n < it.cantidad_enviada ? MODO.DISCREPANCIA : MODO.OK) } : it));
  };

  const handleMotivo = (idx, codigo) => {
    setItems(items.map((it, i) => i === idx ? { ...it, motivo_rechazo_codigo: codigo } : it));
  };

  const handleNota = (idx, value) => {
    setItems(items.map((it, i) => i === idx ? { ...it, nota_item: value } : it));
  };

  const validate = () => {
    for (const it of items) {
      if (it.modo !== MODO.OK && !it.motivo_rechazo_codigo) {
        return `Seleccioná un motivo para "${it.producto_nombre}"`;
      }
    }
    return null;
  };

  const handleConfirm = async () => {
    const validation = validate();
    if (validation) { setError(validation); return; }
    setSaving(true); setError('');
    try {
      const body = {
        recibido_por: localStorage.getItem('id'),
        observaciones_recepcion: observaciones.trim(),
        items: items.map(it => ({
          id: it.id,
          cantidad_recibida: it.cantidad_recibida,
          rechazado: it.modo === MODO.RECHAZADO,
          motivo_rechazo_codigo: it.modo === MODO.OK ? null : it.motivo_rechazo_codigo,
          nota_item: it.nota_item,
        })),
      };
      const res = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/transferencias_mercancia/${transferencia.id}/recibir`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify(body),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Error al procesar');
      onDone?.(result);
      onClose?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const resumen = {
    ok: items.filter(i => i.modo === MODO.OK).length,
    discrepancia: items.filter(i => i.modo === MODO.DISCREPANCIA).length,
    rechazado: items.filter(i => i.modo === MODO.RECHAZADO).length,
  };
  const todosRechazados = items.length > 0 && resumen.rechazado === items.length;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ fontWeight: 700 }}>Recibir {transferencia?.codigo}</DialogTitle>
      <Divider />
      <DialogContent>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Por cada item seleccioná: <strong>Recibido</strong> (todo OK), <strong>Discrepancia</strong> (menos de lo enviado) o <strong>Rechazar</strong>. Los items rechazados o con discrepancia requieren motivo.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {todosRechazados && (
          <Alert severity="warning" sx={{ mb: 2 }}>Todos los items están marcados como rechazados. La transferencia quedará en estado <strong>rechazada</strong> y el stock completo volverá al origen.</Alert>
        )}

        <Stack direction="row" spacing={1} mb={2}>
          <Chip icon={<CheckCircleOutline sx={{ fontSize: 16 }} />} label={`OK · ${resumen.ok}`} color="success" size="small" sx={{ fontWeight: 600 }} />
          <Chip icon={<WarningAmberOutlined sx={{ fontSize: 16 }} />} label={`Discrepancia · ${resumen.discrepancia}`} color="warning" size="small" sx={{ fontWeight: 600 }} />
          <Chip icon={<BlockOutlined sx={{ fontSize: 16 }} />} label={`Rechazados · ${resumen.rechazado}`} color="error" size="small" sx={{ fontWeight: 600 }} />
        </Stack>

        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5, boxShadow: 'none', border: '1px solid', borderColor: 'divider', mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <StyledCellHeader>Producto</StyledCellHeader>
                <StyledCellHeader sx={{ textAlign: 'center', width: 70 }}>Enviado</StyledCellHeader>
                <StyledCellHeader sx={{ textAlign: 'center', width: 260 }}>Acción</StyledCellHeader>
                <StyledCellHeader sx={{ textAlign: 'center', width: 90 }}>Recibido</StyledCellHeader>
                <StyledCellHeader sx={{ width: 220 }}>Motivo</StyledCellHeader>
                <StyledCellHeader>Nota</StyledCellHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((it, idx) => {
                const necesitaMotivo = it.modo !== MODO.OK;
                const serial = esSerializado(it);
                const primary = serial
                  ? (nombreBusqueda({ ...it, nombre: it.producto_nombre }) || it.producto_nombre)
                  : it.producto_nombre;
                const campos = resumenCampos(it, 3, serial ? ['id'] : []);
                return (
                  <TableRow key={it.id} hover>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        {serial && <FingerprintOutlined sx={{ fontSize: 14, color: 'primary.main' }} />}
                        <Typography variant="body2" fontWeight={600}>{primary}</Typography>
                      </Stack>
                      {serial && <Typography variant="caption" color="text.secondary" display="block">{it.producto_nombre}</Typography>}
                      {campos && <Typography variant="caption" color="text.secondary" display="block">{campos}</Typography>}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>{it.cantidad_enviada}</TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      <ToggleButtonGroup
                        size="small" exclusive value={it.modo}
                        onChange={(_, v) => v && handleModo(idx, v)}
                      >
                        <ToggleButton value={MODO.OK} sx={{ textTransform: 'none', px: 1.25, color: it.modo === MODO.OK ? 'success.main' : 'text.secondary' }}>
                          <CheckCircleOutline sx={{ fontSize: 16, mr: 0.5 }} /> OK
                        </ToggleButton>
                        <ToggleButton value={MODO.DISCREPANCIA} sx={{ textTransform: 'none', px: 1.25, color: it.modo === MODO.DISCREPANCIA ? 'warning.main' : 'text.secondary' }}>
                          <WarningAmberOutlined sx={{ fontSize: 16, mr: 0.5 }} /> Discrep.
                        </ToggleButton>
                        <ToggleButton value={MODO.RECHAZADO} sx={{ textTransform: 'none', px: 1.25, color: it.modo === MODO.RECHAZADO ? 'error.main' : 'text.secondary' }}>
                          <BlockOutlined sx={{ fontSize: 16, mr: 0.5 }} /> Rechazar
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      <TextField
                        size="small" type="number"
                        value={it.cantidad_recibida}
                        onChange={(e) => handleCantidad(idx, e.target.value)}
                        disabled={it.modo === MODO.OK || it.modo === MODO.RECHAZADO}
                        sx={{ width: 70, '& .MuiInputBase-input': { textAlign: 'center', fontWeight: 600 } }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small" select fullWidth
                        value={it.motivo_rechazo_codigo}
                        onChange={(e) => handleMotivo(idx, e.target.value)}
                        disabled={!necesitaMotivo}
                        placeholder="Seleccionar"
                      >
                        <MenuItem value="">—</MenuItem>
                        {motivos.map(m => <MenuItem key={m.codigo} value={m.codigo}>{m.label}</MenuItem>)}
                      </TextField>
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small" fullWidth
                        value={it.nota_item}
                        onChange={(e) => handleNota(idx, e.target.value)}
                        placeholder={necesitaMotivo ? 'Detalles (opcional)' : ''}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <TextField
          fullWidth size="small" multiline minRows={2}
          label="Observaciones de recepción"
          value={observaciones} onChange={(e) => setObservaciones(e.target.value)}
        />
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="outlined" onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button
          variant="contained" color={todosRechazados ? 'error' : 'primarybutton'}
          startIcon={todosRechazados ? <BlockOutlined /> : <CheckCircleOutline />}
          onClick={handleConfirm} disabled={saving}
          sx={{ fontWeight: 700 }}
        >
          {saving ? 'Procesando…' : todosRechazados ? 'Rechazar transferencia' : 'Confirmar recepción'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
